<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
    <div v-if="empty==false" class="row overflow-scroll">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h4 class="card-title mb-0">Transactions</h4>
            <base-button
              v-if="hasPermissions('inventory_create') || hasPermissions('transactions_update')"
              size="sm" v-on:click="createTransaction()" type="primary" class="flex-shrink-0 text-nowrap">
              <i class="tim-icons icon-simple-add"></i>
              <span class="d-none d-sm-inline">&nbsp;Create new transaction</span>
              <span class="d-inline d-sm-none">&nbsp;New</span>
            </base-button>
          </div>

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :loading="refreshing"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="transactions"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
            @open-saved-views="onOpenSavedViews"
          >
            <template #actions>
              <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" v-on:click="downloadCSV()">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>


          <div class="tx-empty mt-4" v-if="transactions.length <= 0">
            <img src="/img/pandas/empty-transactions.png" alt="No transactions" class="empty-state-img" />
            <h4 class="tx-empty__title">No transactions found</h4>
            <p class="tx-empty__hint">Transactions will appear here once customers make a purchase.</p>
          </div>
          <div class="row d-block d-sm-none">
            <div class="col">
              <b-table v-if="transactions.length > 0" striped hover :items="transactions"
                       :fields="['transactions']">
                <template v-slot:head(transactions)="row">
                  <span>Total results: {{totalRows}}</span>
                </template>
                <template v-slot:cell(transactions)="row">
                  <div class="row justify-content-between flex-nowrap">
                    <div class="col">
                      <h4 style="padding-bottom: 0px; margin-bottom: 0px" class="text-white d-inline-flex align-items-center">
                        #{{row.item.order_number}}
                        <el-tooltip content="View transaction" effect="light" :open-delay="300" placement="top">
                          <a :href="`/purchases/transactions/details?id=${row.item.id}`" class="tx-id-link__arrow ml-2">
                            <i class="tim-icons el-icon-top-right"></i>
                          </a>
                        </el-tooltip>
                      </h4>
                      <small style="padding-top: 0px" class="text-white d-block" v-if="row.item.timestamp">
                        {{getLocalDate(row.item.timestamp)}}, {{getLocalTime(row.item.timestamp)}}
                      </small>
                    </div>
                    <div class="col-auto d-flex align-items-center" style="gap: 6px;">
                      <badge v-if="row.item.status == 1" type="success">active</badge>
                      <badge v-if="row.item.status == 0" type="danger">cancelled</badge>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-8">
                      <h4 style="padding-bottom: 0px; margin-bottom: 0px" class="text-white">{{row.item.first_name}} {{row.item.last_name}}</h4>
                      <small v-if="row.item.email" class="text-white d-block">
                        <i class="tim-icons icon-email-85 mr-1"></i> {{row.item.email}}
                      </small>
                      <small v-if="row.item.phone" class="text-white d-block">
                        <i class="tim-icons icon-mobile mr-1"></i> {{ formatPhone(row.item.phone, row.item.country) }}
                      </small>
                    </div>
                  </div>
                  <div class="row mt-2" v-if="enable_utm_source_tracking">
                    <div class="col-8">
                      <p class="text-white">
                        <utm-attribution
                          icon="icon-world"
                          :source="row.item.utm_source"
                          :medium="row.item.utm_medium"
                          :campaign="row.item.utm_campaign"
                          :referrer="row.item.utm_referrer"
                          empty-text="-" />
                      </p>
                    </div>
                  </div>
                  <div class="tx-amounts mt-1">
                    <div class="tx-amount">
                      <span class="tx-amount__label">Total</span>
                      <span class="tx-amount__value text-white">{{ getAmount(row.item.total) }}</span>
                    </div>
                    <div class="tx-amount">
                      <span class="tx-amount__label">Paid</span>
                      <span class="tx-amount__value text-white">{{ getAmount(row.item.paid) }}</span>
                    </div>
                    <div class="tx-amount">
                      <span class="tx-amount__label">Due</span>
                      <span class="tx-amount__value" :class="row.item.due === 0 ? 'text-white' : 'text-danger'">{{ getAmount(row.item.due) }}</span>
                    </div>
                  </div>

                  <div class="row mt-3" v-if="row.item.status === 1">
                    <div class="col">
                      <base-button variant="outlined" tone="danger" @click="cancelTransactionPrompt(row.item, $event)">
                        <i class="tim-icons icon-trash-simple"></i> Cancel transaction
                      </base-button>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>
          </div>

          <div class="row d-none d-sm-block">
            <div class="col">
              <b-table v-if="transactions.length > 0" striped hover :items="transactions"
                       :fields="fields">
                <template v-slot:cell(transaction_id)="row">
                  <p class="text-white">#{{row.item.order_number}}</p>
                </template>
                <template v-slot:cell(transaction_date)="row">
                  <p class="text-white">{{getLocalDate(row.item.timestamp)}}, {{getLocalTime(row.item.timestamp)}}</p>
                </template>
                <template v-slot:cell(customer)="row">
                  <p class="text-white mb-0">{{row.item.first_name}} {{row.item.last_name}}</p>
                  <small v-if="row.item.email" class="text-white d-block">
                    <i class="tim-icons icon-email-85 mr-1"></i>{{row.item.email}}
                  </small>
                  <small v-if="row.item.phone" class="text-white d-block">
                    <i class="tim-icons icon-mobile mr-1"></i>{{ formatPhone(row.item.phone, row.item.country) }}
                  </small>
                  <p v-if="!row.item.email && !row.item.phone" class="text-white mb-0">-</p>
                </template>
                <template v-slot:cell(total)="row">
                  <p class="text-white">{{getAmount(row.item.total)}}</p>
                </template>
                <template v-slot:cell(paid)="row">
                  <p class="text-white">{{getAmount(row.item.paid)}}</p>
                </template>
                <template v-slot:cell(due)="row">
                  <p v-if="row.item.due === 0" class="text-white">{{getAmount(row.item.due)}}</p>
                  <p v-if="row.item.due > 0 || row.item.due < 0" class="text-danger">{{getAmount(row.item.due)}}</p>
                </template>
                <template v-slot:cell(status)="row">
                  <badge v-if="row.item.status == 1" type="success">active</badge>
                  <badge v-if="row.item.status == 0" type="danger">cancelled</badge>
                </template>
                <template v-slot:cell(source)="row">
                  <utm-attribution
                    icon="icon-world"
                    :source="row.item.utm_source"
                    :medium="row.item.utm_medium"
                    :campaign="row.item.utm_campaign"
                    :referrer="row.item.utm_referrer"
                    empty-text="-" />
                </template>
                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button
                      simple
                      type="primary"
                      link
                    > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item mt-1 mb-2" target="_blank" :href="'/purchases/transactions/details?id=' + row.item.id">
                          <i class="tim-icons icon-notes text-default mr-2"></i> View transaction details
                        </a>
                        <hr v-if="row.item.status === 1">
                        <a v-if="row.item.status === 1" class="dropdown-item mb-2" @click="cancelTransactionPrompt(row.item, $event)" href="#">
                          <i class="tim-icons icon-trash-simple text-default mr-2"></i> Cancel transaction
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
            </div>
          </div>

          <b-pagination v-on:input="onPageChange()"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
          ></b-pagination>
        </card>
      </div>
    </div>

    <cancel-transaction-drawer
      :show="cancelDrawerOpen"
      :transaction-id="cancelTransactionId"
      @close="cancelDrawerOpen = false"
      @cancelled="fetchTransactions(false, true)"
    />

  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import swal from "sweetalert2";
import { BTable, BPagination } from 'bootstrap-vue'
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import {formatPhoneDisplay} from "@/util/phoneFormatter";
import {Badge} from '@/components';
import CancelTransactionDrawer from '@/components/Purchases/CancelTransactionDrawer.vue';
import axios from "axios";
import currencies from "@/assets/currencies.json";
import _ from "lodash";
import {Dropdown, DropdownItem, DropdownMenu} from "element-ui";


export default {
  name: "Transactions",
  components: {
    BTable,
    Badge,
    BPagination,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    CancelTransactionDrawer
  },
  data() {
    return {
      enable_utm_source_tracking: false,
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      perPageOptions: [5, 10, 25, 50],
      empty: false,
      loading: false,
      refreshing: false,
      transactions: [],
      cancelDrawerOpen: false,
      cancelTransactionId: null,
      fields: ['transaction_id', 'transaction_date', 'customer', 'total', 'paid', 'due', 'status', 'actions'],
      currentPage: 1,
      totalRows: 0,
      perPage: 10,
      filterValues: this.buildInitialFilterValues(),
      // FilterBar (saved-views enabled) drives the first fetch via its initial
      // `change`; this guards the page number on that first call.
      didInitialFetch: false,
    }
  },
  computed: {
    filterSchema() {
      // Primary chips: always visible
      const schema = [
        {key: 'order_number', type: 'text', label: 'Order number', placeholder: 'Order number', operatorLabel: 'contains', primary: true},
        {key: 'name', type: 'text', label: 'Customer name', placeholder: 'Name', operatorLabel: 'contains', primary: true},
        {key: 'email', type: 'text', label: 'Email', placeholder: 'Email', operatorLabel: 'contains', primary: true},
        {key: 'date', type: 'daterange', label: 'Transaction date', primary: true},
      ]
      // Secondary: behind "More filters"
      schema.push({key: 'status', type: 'select', label: 'Status', options: [
        {label: 'Active', value: 'active'},
        {label: 'Cancelled', value: 'cancelled'},
      ]})
      schema.push({key: 'payment', type: 'select', label: 'Payment', options: [
        {label: 'Due', value: 'due'},
        {label: 'Fully paid', value: 'paid'},
      ]})
      schema.push({key: 'phone', type: 'text', label: 'Phone', placeholder: 'Phone number', operatorLabel: 'contains'})
      if (this.enable_utm_source_tracking) {
        schema.push({key: 'source', type: 'text', label: 'Source', placeholder: 'Source', operatorLabel: 'contains'})
      }
      return schema
    }
  },
  methods: {
    buildInitialFilterValues() {
      const q = this.$route.query || {}
      return {
        order_number: q.transaction_id || '',
        name: q.name || '',
        email: q.email || '',
        phone: q.phone || '',
        source: q.source || '',
        payment: q.payment || '',
        status: q.status || '',
        date: {
          mode: 'range',
          days: '',
          from: q.from || '',
          to: q.to || '',
        },
      }
    },
    computeDateRange() {
      // Derive ISO from/to from the current daterange filter value.
      const d = this.filterValues.date || {}
      let to = ''
      let from = ''
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        let today = new Date()
        let dt = new Date()
        dt.setDate(dt.getDate() - Number(d.days))
        let userStartDate = dt.getFullYear() + "-" + ('0' + (dt.getMonth() + 1)).slice(-2) + "-" + ('0' + dt.getDate()).slice(-2)
        let userEndDate = today.getFullYear() + "-" + ('0' + (today.getMonth() + 1)).slice(-2) + "-" + ('0' + today.getDate()).slice(-2)
        from = new Date(`${userStartDate}T00:00:00`).toISOString()
        to = new Date(`${userEndDate}T23:59:59`).toISOString()
      } else if (d.mode === 'range' && d.from && d.to) {
        from = new Date(`${d.from}T00:00:00`).toISOString()
        to = new Date(`${d.to}T23:59:59`).toISOString()
      }
      return {to, from}
    },
    async fetchTransactions(resetPage, silent) {
      if (resetPage) {
        this.currentPage = 1
      }
      const {to, from} = this.computeDateRange()
      const count = this.perPage
      const offset = parseInt((this.currentPage * this.perPage) - this.perPage)
      await this.getTransactions(count, offset, this.filterValues.order_number, this.filterValues.name,
        this.filterValues.email, to, from, this.filterValues.status, this.filterValues.payment, this.filterValues.source, this.filterValues.phone, silent)
    },
    onFiltersChanged(values) {
      this.filterValues = {...this.filterValues, ...values, date: {...values.date}}
      // The first change is FilterBar's initial saved-views resolution; keep the
      // page number the URL restored. Later changes reset to page 1.
      if (!this.didInitialFetch) {
        this.didInitialFetch = true
        this.fetchTransactions(false, true)
      } else {
        this.fetchTransactions(true, true)
      }
    },
    onOpenSavedViews() {
      // Wired to the SavedViewsDrawer in M2.
    },
    onPerPageChange(n) {
      this.perPage = n
      this.fetchTransactions(true, true)
    },
    onPageChange() {
      this.fetchTransactions(false, true)
    },
    decapitalizeFirstLetterAddUnderscore(string) {
      if (!string) {
        return string;
      }
      string = string.replace(/ /g, '_');
      return string.charAt(0).toLowerCase() + string.slice(1);
    },
    capitalizeFirstLetter(str) {
      if (!str) return '';
      str = str.replace(/_/g, ' ');
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    formatPhone(phone, country) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    async createTransaction(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/transactions/initial'
      let data = {}


      this.axios.post(url, data, config)
        .then(response => {
          this.$router.push({ path: '/purchases/transactions/details', query: { id:  response.data }})
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    cancelTransactionPrompt(item, event) {
      event.preventDefault()
      this.cancelTransactionId = item.id
      this.cancelDrawerOpen = true
    },
    getStatus(status){
      if(status === 1){
        return 'Active'
      } else {
        return 'Cancelled'
      }
    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        // If the value contains a comma, wrap it in double quotes
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`;
        }
      }
      return value;
    },
    async downloadCSV(){
      //get the entire data set
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      this.loading = true
      let count = 5000
      const {to, from} = this.computeDateRange()
      const csvData = await this.getAllTransactions(config, count, this.filterValues.order_number, this.filterValues.name,
        this.filterValues.email, to, from, this.filterValues.status, this.filterValues.payment, this.filterValues.source, this.filterValues.phone)
      this.loading = false

      let csv = 'Transaction Id,Transaction Date,Customer Name,Customer Email,Total,Paid,Due,Status,UTM Source,UTM Medium,UTM Campaign,UTM Referrer\n';
      csvData.forEach(row => {
        csv += [this.escapeCSVValue(row.order_number),
          this.escapeCSVValue(this.getLocalDateNoDay(row.timestamp)),
          this.escapeCSVValue(row.first_name + ' ' + row.last_name),
          this.escapeCSVValue(row.email),
          row.total.toFixed(2),
          row.paid.toFixed(2),
          row.due.toFixed(2),
          this.escapeCSVValue(this.getStatus(row.status)),
          this.escapeCSVValue(this.capitalizeFirstLetter(row.utm_source) || ''),
          this.escapeCSVValue(row.utm_medium || ''),
          this.escapeCSVValue(row.utm_campaign || ''),
          this.escapeCSVValue(row.utm_referrer || '')]
        csv += "\n";
      });

      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = 'transactions.csv';
      anchor.click();
    },
    async getAllTransactions(config, count, t_id, name, email, to, from, status, payment, source, phone){
      let url = ''
      let promises = []
      let csvData = []
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id


      let offset = 0
      while(offset <= this.totalRows){

        url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/transactions?count=' + count + "&offset=" + offset +
           "&name=" + encodeURIComponent(name) + "&email=" + encodeURIComponent(email) + "&phone=" + encodeURIComponent(phone)
          + "&payment=" + encodeURIComponent(payment) + "&to=" + to + "&from=" + from + "&t_id=" + encodeURIComponent(t_id) + "&status=" + status + "&source=" + encodeURIComponent(this.decapitalizeFirstLetterAddUnderscore(source))

        promises.push(
          axios.get(url, config)
            .then(response =>{
              csvData.push(response.data.transactions)
            })
            .catch(err => {
                console.log(err)
                if (err.response.status == 401) {
                  swal({
                    title: 'Error',
                    html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                    type: 'error',
                  }).then((response)=> {
                    this.$router.push('/')
                  })
                } else {
                  swal('Error!', 'Something went wrong', 'error')
                }
              }
            )
        )
        offset = offset + Number(count)
      }

      await Promise.all(promises)

      // merge all response arrays into one
      let arr = []
      csvData.forEach((element) => {
        arr = arr.concat(element)

      })
      return arr
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    getLocalDateNoDay(d){
      return formatGroupDate(d, { dayOfWeek: false }) + '  ' + formatGroupTime(d)
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    getAmount(integer) {
      if (integer == null) {
        return;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    async getTransactions(count, offset, t_id, name, email, to, from, status, payment, source, phone, silent){
      if(name === undefined){
        name = ''
      }
      if(phone === undefined){
        phone = ''
      }
      if(email === undefined){
        email = ''
      }
      if(to === undefined){
        to = ''
      }
      if(from === undefined){
        from = ''
      }
      if(status === undefined){
        status = ''
      }
      if(t_id === undefined){
        t_id = ''
      }
      if(payment === undefined){
        payment = ''
      }
      if(source === undefined){
        source = ''
      }
      // Filter/pagination refetches are "silent" — a subtle inline spinner in the
      // filter bar instead of the full-screen loading overlay (which steals input focus).
      if (silent) {
        this.refreshing = true
      } else {
        this.loading = true
      }
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId  + '/transactions?count=' + count + "&offset=" + offset
        + "&name=" + encodeURIComponent(name) + "&email=" + encodeURIComponent(email) + "&phone=" + encodeURIComponent(phone)
        + "&payment=" + encodeURIComponent(payment) + "&to=" + to + "&from=" + from + "&t_id=" + encodeURIComponent(t_id) + "&status=" + status  + "&source=" + encodeURIComponent(this.decapitalizeFirstLetterAddUnderscore(source))
      this.axios.get(url, config)
        .then(response => {
          this.transactions = response.data.transactions
          this.totalRows = response.data.totalRows
          this.enable_utm_source_tracking = response.data.enable_utm_source_tracking
          if(this.enable_utm_source_tracking){
            this.fields = ['transaction_id', 'transaction_date', 'customer', 'source', 'total', 'paid', 'due', 'status', 'actions']
          }
          this.addQueryParameters()
          this.loading = false
          this.refreshing = false
        })
        .catch(err => {
          this.loading = false
          this.refreshing = false
          console.log(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }else if(err.response.status == 500){
            swal({
              title: 'Ooops',
              text: 'Booking does not exist',
              type: 'error',
              confirmButtonColor: '#fd5d93',
              confirmButtonText: 'Back to bookings'
            }).then((response)=> {
              if(response.value == true){
                this.$router.push('/bookings')
              }
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    addQueryParameters() {
      const d = this.filterValues.date || {}
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        name: this.filterValues.name,
        source: this.filterValues.source,
        email: this.filterValues.email,
        phone: this.filterValues.phone,
        to: d.mode === 'range' ? d.to : '',
        from: d.mode === 'range' ? d.from : '',
        payment: this.filterValues.payment,
        transaction_id: this.filterValues.order_number,
        status: this.filterValues.status
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
  },
  mounted(){
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    // FilterBar owns the initial applied filter state (saved-views precedence) and
    // emits one initial `change` that drives the first fetch — see onFiltersChanged.
    // Show the loading overlay until that lands.
    this.loading = true
  }
}
</script>

<style scoped>

</style>

<style>

/* Light mode: drop the table zebra stripes for a cleaner look */
.white-content .table-striped tbody tr:nth-of-type(odd),
.white-content .table-striped tbody tr:nth-of-type(odd) > td {
  background-color: transparent;
}

/* Mobile: open-in-detail arrow next to the transaction id (replaces the row menu) */
.tx-id-link__arrow {
  color: #e14eca;
  transition: opacity 0.15s ease;
}
.tx-id-link__arrow:hover {
  color: #e14eca;
  opacity: 0.7;
}

/* Empty state */
.tx-empty {
  text-align: center;
  padding: 2.5rem 1rem;
}
.tx-empty__title {
  color: #fff;
  margin-bottom: 0.25rem;
}
.tx-empty__hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 420px;
  margin: 0 auto;
}
.white-content .tx-empty__title {
  color: #344675;
}
.white-content .tx-empty__hint {
  color: rgba(0, 0, 0, 0.55);
}

/* Mobile: compact Total / Paid / Due summary row */
.tx-amounts {
  display: flex;
  gap: 12px;
}
.tx-amount {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tx-amount__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: rgba(125, 125, 125, 0.9);
}
.tx-amount__value {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
}
</style>
